import type { MutableRefObject, ReactNode } from 'react'
import React, {
  createContext,
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

type BoundaryRef = MutableRefObject<HTMLDivElement | null>

export const PopoverBoundaryContext = createContext<BoundaryRef>(createRef())

export const PopoverRootBoundaryContext = createContext<BoundaryRef | null>(
  null
)

type PopoverStoreItem = {
  closeOnEscape: boolean
  close: () => void
}

type PopoverStore = {
  register: (item: PopoverStoreItem) => void
  unregister: (item: PopoverStoreItem) => void
}

export const PopoverStoreContext = createContext<PopoverStore>({
  register: () => {},
  unregister: () => {},
})

type PopoverBoundaryProps = {
  children: (ref: BoundaryRef) => ReactNode
  parentRef?: BoundaryRef
}

/**
 * This component allows to set the element that will be used as a boundary for
 * all Popover components bellow it. This will make sure that your popovers are
 * contained within certain parameters.
 *
 * You can find more information about popover boundaries here:
 * https://github.com/alexkatz/react-tiny-popover#api
 */
export const PopoverBoundary = ({
  children,
  parentRef,
}: PopoverBoundaryProps) => {
  const boundaryRef = useRef<HTMLDivElement | null>(null)
  const ref = parentRef || boundaryRef
  const outerRootBoundaryRef = useContext(PopoverRootBoundaryContext)
  const rootBoundaryRef = outerRootBoundaryRef ?? ref

  const storeRef = useRef<PopoverStoreItem[]>([])
  const store = useMemo(() => {
    return {
      register: (item: PopoverStoreItem) => {
        storeRef.current.push(item)
      },
      unregister: (item: PopoverStoreItem) => {
        storeRef.current = storeRef.current.filter((i) => i !== item)
      },
    }
  }, [])

  useEffect(() => {
    function closeLastRegistered(event: KeyboardEvent) {
      if (
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        storeRef.current.length > 0
      ) {
        event.preventDefault()
        event.stopPropagation()
        const lastItem = storeRef.current.at(-1)
        if (lastItem?.closeOnEscape) {
          storeRef.current.pop()?.close()
        }
      }
    }

    document.body.addEventListener('keyup', closeLastRegistered)
    return function onLeave() {
      document.body.removeEventListener('keyup', closeLastRegistered)
    }
  }, [])

  return (
    <PopoverBoundaryContext.Provider value={ref}>
      <PopoverRootBoundaryContext.Provider value={rootBoundaryRef}>
        <PopoverStoreContext.Provider value={store}>
          {children(ref)}
        </PopoverStoreContext.Provider>
      </PopoverRootBoundaryContext.Provider>
    </PopoverBoundaryContext.Provider>
  )
}
